import {
	dateToUTCString,
	startOf,
	endOf,
	addUnit,
	isBefore,
	duration,
	fromNow,
	utcTimestampToLocalDate,
	formatDate,
	localOrUtcTimestampToLocalDate,
	getDiff,
	parseDate,
} from "@sembark-travel/datetime-utils"
import {
	Box,
	Button,
	Icons,
	Inline,
	Heading,
	Stack,
	Text,
	Col,
	Divider,
	Grid,
	Component,
	Alert,
	Table,
	TimeDuration,
	Time,
	joinAttributes,
	Money,
	RelativeTime,
	Badge,
	Select,
	Tooltip,
	Ping,
	useBreakpoints,
} from "@sembark-travel/ui-base"
import { IListResponse, useXHR, XHRInstance } from "@sembark-travel/xhr"
import * as Validator from "yup"
import { SelectTripDestination, TTripDestination } from "../TripDestinations"
import {
	ITripSource,
	SelectTripSourceContact,
	SelectTripSources,
} from "./../TripSources"
import { ITrip } from "./store"
import {
	Form,
	Field,
	TextInputField,
	TextAreaInputField,
	SelectField,
	validateFormValues,
	withServerErrors,
	DatePickerField,
	SubmissionError,
	PhoneInputField,
	GetFieldValue,
	isTruthy,
	EmptyNumberValidator,
	PhoneNumberValidator,
	useFieldValue,
} from "@sembark-travel/ui-form"
import { useMemo, useState } from "react"
import pluralize from "pluralize"
import { PhoneNumber, type IContact, Email } from "../Contacts"
import { type TChildrenArray, ChildrenInputField } from "./../Tourists"
import { SelectUsers } from "../Users"
import { IUser, PERMISSIONS, useAuthUser, useCheckPermissions } from "../Auth"
import {
	TAddressInputFieldValue,
	transformAddressToEditableValue,
	transformAddressToStorableValue,
} from "./../Addresses"
import { AddressInputField } from "../Addresses"
import { ITag, SelectTags, TagBadges } from "../Tags"
import { SelectWorldCountries } from "../World"
import { ILocation } from "../Locations"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { TTouristProfile } from "../TouristProfiles"
import { TripTourist } from "."
import { Destinations, IDCell } from "./List"
import useSWR from "swr"
import { useDebounce } from "../Hotels"
import { Link } from "@sembark-travel/ui-router"
import { generatePath } from "../router-utils"

function XHR(xhr: XHRInstance) {
	return {
		async createTrip(data: unknown): Promise<ITrip> {
			return xhr.post("/trips", data).then((resp) => resp.data.data)
		},
	}
}

export function AddNewTrip({
	onSuccess,
	...props
}: Omit<React.ComponentProps<typeof TripDetailsForm>, "onSubmit"> & {
	onSuccess: (trip: ITrip) => void
}) {
	const xhr = useXHR()
	return (
		<TripDetailsForm
			showTeam
			{...props}
			onSubmit={async (data) => {
				const trip = await XHR(xhr).createTrip(data)
				// ignore the exception
				await Promise.resolve(onSuccess(trip)).catch(() => undefined)
			}}
		/>
	)
}

const validationSchema = Validator.object().shape({
	trip_id: Validator.string(),
	start_date: Validator.date().required("Start date is required"),
	no_of_days: EmptyNumberValidator()
		.positive("Number of days should be a positive integer e.g. 1, 2, 3 etc")
		.integer("Number of days should be a positive integer e.g. 1, 2, 3 etc.")
		.required("Number of days is required"),
	no_of_nights: EmptyNumberValidator()
		.integer("Number of nights should be an integer e.g. 0, 1, 2 etc.")
		.min(0, "Number of nights should not be negative")
		.required("Number of nights is required"),
	destinations: Validator.array().min(
		1,
		"Please select atleast one destination"
	),
	no_of_adults: EmptyNumberValidator()
		.positive("Number of adults should be a positive integer")
		.integer("Number of adults should be a positive integer")
		.required("Number of adults field is required"),
	complimentary_count: EmptyNumberValidator()
		.min(0, "Total FOC should be a non-negative integer")
		.integer("Total FOC should be a non-negative integer")
		.test(
			"max-complimentary_count",
			"Total FOC must be less than the number of guests",
			function (value) {
				const { no_of_adults, children } = this.parent as NewItemSchema
				const chidlrenCount = children.filter((c) => c.age).length
				const totalPaxCount = no_of_adults + chidlrenCount
				if (value === undefined || value === null) return true
				return value < totalPaxCount
			}
		),
	children: Validator.array().of(
		Validator.object().shape({
			count: EmptyNumberValidator()
				.positive("Number of children should be positive integer")
				.integer("Number of children should be positive integer"),
			age: EmptyNumberValidator().positive(
				"Child age should a positive number"
			),
		})
	),
	trip_source: Validator.object().required("Query Source is required"),
	trip_source_contact: Validator.object().nullable(),
	tourist: Validator.object().when(
		["trip_source", "trip_source_contact"],
		([tripSource, tripSourceContact], schema) => {
			const nameValidator = Validator.string().max(
				191,
				"Please use 191 or fewer character for name"
			)
			const emailValidator = Validator.string().email(
				"Please provide a valid email address"
			)
			const phoneValidator = Validator.array().when(
				"name",
				([touristName], schema) => {
					if (touristName) {
						return schema
							.of(PhoneNumberValidator("Phone Number", false))
							.min(1, "Please provide atleast one phone number")
					}
					return schema.of(PhoneNumberValidator("Phone Number", true))
				}
			)
			return schema.shape({
				name: tripSourceContact
					? nameValidator.nullable()
					: nameValidator.required(
							tripSource && isTruthy(tripSource.is_agent)
								? "Please provide guest details or select/add a contact from query source"
								: "Please provide the guest details"
						),
				email: emailValidator,
				phone_numbers: phoneValidator,
			})
		}
	),
	comments: Validator.string().nullable(),
	tags: Validator.array()
		.of(
			Validator.object().shape({
				name: Validator.string().required("Tag name is required"),
			})
		)
		.optional(),
})

const validate = validateFormValues(validationSchema)

interface NewItemSchema {
	reference_id?: string
	start_date?: Date
	no_of_days: number
	no_of_nights: number
	destinations: Array<TTripDestination>
	no_of_adults: number
	complimentary_count?: number
	trip_source?: ITripSource
	children: TChildrenArray
	comments?: string
	trip_source_contact?: IContact
	sales_team?: IUser[]
	tourist?: {
		type?: "adult" | "child"
		salutation?: "Mr." | "Mrs." | "Ms."
		name?: string
		phone_numbers: Array<{
			number: string
			country_code?: string
			is_primary?: boolean
		}>
		email?: string
		address?: TAddressInputFieldValue
		nationality?: ILocation["country"]
		dob?: Date
		remarks?: string
		profile_id?: number | null
		profile?: TTouristProfile
	}
	tags: Array<ITag>
}

const defaultInitialValues: NewItemSchema = {
	reference_id: "",
	start_date: undefined,
	no_of_days: 2,
	no_of_nights: 1,
	destinations: [],
	no_of_adults: 1,
	complimentary_count: 0,
	children: [],
	trip_source: undefined,
	comments: "",
	tourist: {
		type: "adult",
		salutation: undefined,
		name: "",
		phone_numbers: [],
		email: "",
		address: undefined,
		nationality: undefined,
		dob: undefined,
		remarks: undefined,
		profile_id: null,
		profile: undefined,
	},
	trip_source_contact: undefined,
	sales_team: [],
	tags: [],
}

export function TripDetailsForm({
	onSubmit,
	onCancel,
	initialValues: initialValuesFromProps,
	readOnlySource,
	readOnlyDates,
	readOnlyPax,
	hideComments,
	hideTags,
	showTeam,
	showDuplicateExceptId,
}: {
	onSubmit: (data: unknown) => Promise<void>
	onCancel?: () => void
	initialValues?: Partial<NewItemSchema>
	readOnlySource?: boolean
	readOnlyDates?: boolean
	readOnlyPax?: boolean
	hideComments?: boolean
	hideTags?: boolean
	showTeam?: boolean
	showDuplicateExceptId?: number
}) {
	const { user } = useAuthUser()
	const [initialValues] = useState<NewItemSchema>(() => {
		const payload: NewItemSchema = {
			...defaultInitialValues,
			...initialValuesFromProps,
			sales_team:
				initialValuesFromProps?.sales_team || (user ? [user] : undefined),
			tourist: initialValuesFromProps?.tourist || defaultInitialValues.tourist,
			no_of_nights: initialValuesFromProps?.no_of_days
				? initialValuesFromProps?.no_of_days - 1
				: defaultInitialValues.no_of_nights,
		}
		return payload
	})

	const { xs, sm, md } = useBreakpoints()
	const shouldRemoveGutters = xs || sm || md

	return (
		<Form<NewItemSchema>
			initialValues={initialValues}
			validate={validate}
			onSubmit={withServerErrors(async (values) => {
				const {
					start_date,
					no_of_adults,
					no_of_days,
					children = [],
					destinations,
					trip_source,
					reference_id,
					tourist,
					trip_source_contact,
					comments,
					sales_team,
					complimentary_count,
				} = values
				if (
					start_date &&
					no_of_days &&
					no_of_adults &&
					destinations &&
					destinations.length
				) {
					let calculatedAge: number | undefined = undefined
					if (tourist?.dob && start_date) {
						try {
							calculatedAge = getDiff(
								parseDate(start_date),
								parseDate(tourist.dob),
								"years"
							)
						} catch (e) {
							calculatedAge = undefined
						}
					}

					const data = {
						start_date: dateToUTCString(startOf(start_date, "day")),
						start_date_local: formatDate(
							startOf(start_date, "day"),
							"YYYY-MM-DD"
						),
						end_date: dateToUTCString(
							endOf(addUnit(start_date, no_of_days - 1, "day"), "day")
						),
						end_date_local: formatDate(
							endOf(addUnit(start_date, no_of_days - 1, "day"), "day"),
							"YYYY-MM-DD"
						),
						no_of_adults,
						complimentary_count: complimentary_count ? complimentary_count : 0,
						children: children,
						destinations: destinations.map((destination) => destination.id),
						reference_id: reference_id,
						trip_source: trip_source?.id,
						trip_source_contact: trip_source_contact?.id,
						comments,
						sales_team: sales_team?.map((s) => s.id),
						tourist: tourist?.name
							? {
									type: tourist.type,
									salutation: tourist.salutation,
									name: tourist.name,
									phone_numbers: tourist.phone_numbers.map((p) => ({
										...p,
										is_primary: isTruthy(p.is_primary) ? 1 : 0,
									})),
									email: tourist.email || undefined,
									address: tourist.address?.location
										? {
												...transformAddressToStorableValue(tourist.address),
												addressing_name: tourist.name,
												billing_details: tourist.address.billing_details,
											}
										: undefined,
									nationality_id: tourist.nationality?.id ?? undefined,
									dob: tourist.dob
										? formatDate(tourist.dob, "YYYY-MM-DD")
										: undefined,
									age_during_trip: calculatedAge ? calculatedAge : undefined,
									remarks: tourist.remarks ?? undefined,
									profile_id: tourist.profile_id ?? null,
								}
							: null,
						tags: values.tags.map((tag) => tag.name),
					}
					await onSubmit(data)
				} else {
					throw new Error("Please fill the required attributes")
				}
			})}
			subscription={{
				submitting: true,
			}}
		>
			{({ submitting, handleSubmit, form }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Grid gap="4">
						<Col sm={12} md={4}>
							<Stack gap="1">
								<Heading as="h3">
									<Icons.InboxIn size="6" /> Query Source
								</Heading>
								<Text color="muted">
									Please specify the query source, e.g., whether it came via B2B
									or from another source.
								</Text>
							</Stack>
						</Col>
						<Col>
							<Stack gap="4">
								<Grid gap="4">
									<Col xs={12} sm>
										<SelectField
											select={SelectTripSources}
											name="trip_source"
											label="Query Source"
											help={
												<GetFieldValue<
													ITripSource | undefined
												> name="trip_source">
													{({ value }) => (
														<>
															{value && (
																<Inline
																	gap="1"
																	alignItems="center"
																	flexWrap="wrap"
																>
																	<Text fontWeight="semibold">
																		{value.address?.location.name}
																	</Text>

																	<TagBadges
																		collapseBelow="sm"
																		tags={value.tags}
																	/>
																</Inline>
															)}
														</>
													)}
												</GetFieldValue>
											}
											includeContacts
											required
											multiple={false}
											fetchOnMount={!initialValues.trip_source}
											creatable
											disabled={readOnlySource}
											onChange={(value: ITripSource | undefined) => {
												form.change("trip_source", value)
												if (value?.contacts?.length === 1 && value.is_agent) {
													form.change(
														"trip_source_contact",
														value?.contacts?.at(0)
													)
												} else {
													form.change("trip_source_contact", undefined)
												}
											}}
											onCreateSuccess={(tripSource: ITripSource) => {
												form.change("trip_source", tripSource)
												if (
													tripSource?.contacts?.length === 1 &&
													tripSource.is_agent
												) {
													form.change(
														"trip_source_contact",
														tripSource?.contacts?.at(0)
													)
												} else {
													form.change("trip_source_contact", undefined)
												}
											}}
										/>
									</Col>
									<GetFieldValue<ITripSource | null> name="trip_source">
										{({ value }) =>
											value &&
											(isTruthy(value.is_agent) ||
												initialValues.trip_source_contact) ? (
												<Col>
													<SelectField
														tripSourceId={value.id}
														select={SelectTripSourceContact}
														name="trip_source_contact"
														label="Contact / Enquiry Person"
														required
														creatable
														disabled={readOnlySource}
														onCreateSuccess={(contact) => {
															form.change("trip_source_contact", contact)
														}}
													/>
												</Col>
											) : null
										}
									</GetFieldValue>
								</Grid>
								<Grid gap="4">
									<Col>
										<Stack gap="4">
											<Box>
												<TextInputField
													name="reference_id"
													type="text"
													label="Reference ID"
													secondaryLabel="optional"
													placeholder="1231231"
													help="A custom id for your reference regarding the query"
													maxWidth="xs"
												/>
											</Box>
											{!hideTags ? (
												<Box>
													<SelectField
														name="tags"
														select={SelectTags}
														creatable={false}
														label="Tags"
														secondaryLabel="optional"
														type="trips"
														multiple
														placeholder="Search & select tags(s)..."
														fetchOnMount
													/>
												</Box>
											) : null}
										</Stack>
									</Col>
									{showTeam ? (
										<Col>
											<Component initialState={false}>
												{({ state, setState }) => (
													<Box>
														{!state ? (
															<Box>
																<Stack gap="1">
																	<Text fontWeight="semibold">Sales Team</Text>
																	<Box>
																		<Button
																			onClick={() => setState(true)}
																			level="secondary"
																			title="Click to Edit"
																		>
																			You <Icons.Pencil opacity="50" />
																		</Button>
																	</Box>
																</Stack>
															</Box>
														) : (
															<SelectField
																name="sales_team"
																select={SelectUsers}
																label="Sales Team"
																multiple
																autoFocus
																fetchOnMount
															/>
														)}
													</Box>
												)}
											</Component>
										</Col>
									) : null}
								</Grid>
							</Stack>
						</Col>
					</Grid>
					<Divider />
					<Grid gap="4">
						<Col sm={12} md={4}>
							<Stack gap="1">
								<Heading as="h3">
									<Icons.LocationMarker size="6" /> Destination and Duration
								</Heading>
								<Text color="muted">
									Please provide basic details such as destination, duration
									etc. along with number of adults and children with ages
								</Text>
							</Stack>
						</Col>
						<Col>
							<Stack gap="4">
								<Grid gap="4">
									<Col sm={12} md="auto">
										<SelectField
											name="destinations"
											label="Destinations"
											select={SelectTripDestination}
											multiple
											fetchOnMount={!initialValues.destinations?.length}
											closeOnSelect
										/>
									</Col>
									<Col md="auto">
										<Grid gap="4">
											<Col xs="auto">
												<DatePickerField
													name="start_date"
													label="Start Date"
													required
													disabled={readOnlyDates}
													help={
														<Field<Date | undefined>
															name="start_date"
															subscription={{ value: true }}
														>
															{({ input: { value: start_date } }) =>
																start_date ? (
																	<>
																		{isBefore(
																			start_date,
																			startOf(new Date(), "day")
																		) ? (
																			<Box color="warning" as="span">
																				<Icons.Attention /> You have selected a
																				past date.
																			</Box>
																		) : null}
																	</>
																) : null
															}
														</Field>
													}
												/>
											</Col>
											<Col xs="auto">
												<TextInputField
													name="no_of_nights"
													label="No. of Nights"
													type="number"
													min={0}
													required
													disabled={readOnlyDates}
													style={{ maxWidth: "140px" }}
													onChange={(e) => {
														form.batch(() => {
															form.change(
																"no_of_nights",
																e.currentTarget.value as never as number
															)
															form.change(
																"no_of_days",
																isNaN(parseInt(e.currentTarget.value))
																	? 0
																	: parseInt(e.currentTarget.value) + 1
															)
														})
													}}
													help={
														<Field<Date | undefined>
															name="start_date"
															subscription={{ value: true }}
														>
															{({ input: { value: start_date } }) => (
																<Field<number | undefined>
																	name="no_of_days"
																	subscription={{ value: true }}
																>
																	{({ input: { value: no_of_days } }) =>
																		no_of_days ? (
																			<Stack gap="1" flexWrap="wrap">
																				<Text
																					fontWeight="semibold"
																					fontSize="base"
																				>
																					{no_of_days > 1
																						? pluralize(
																								"Night",
																								no_of_days - 1,
																								true
																							) + ", "
																						: ""}
																					{pluralize("Day", no_of_days, true)}
																				</Text>
																				{start_date &&
																				isBefore(
																					addUnit(
																						start_date,
																						no_of_days,
																						"days"
																					),
																					startOf(new Date(), "day")
																				) ? (
																					<Box color="warning" as="span">
																						<Icons.Attention /> You have
																						selected a past date.
																					</Box>
																				) : null}
																			</Stack>
																		) : null
																	}
																</Field>
															)}
														</Field>
													}
												/>
											</Col>
										</Grid>
									</Col>
								</Grid>
								<Grid gap="4">
									<Col xs="auto">
										<TextInputField
											name="no_of_adults"
											label="No. of Adults"
											type="number"
											min={1}
											max={1000}
											required
											disabled={readOnlyPax}
											style={{ maxWidth: "100px" }}
										/>
									</Col>
									<Col xs="auto">
										<GetFieldValue<
											NewItemSchema["destinations"]
										> name="destinations">
											{({ value: tripDestinations }) => (
												<ChildrenInputField
													name="children"
													disabled={readOnlyPax}
													maxAge={
														tripDestinations.length
															? Math.max(
																	...tripDestinations.map(
																		(d) => d.max_child_age || 12
																	)
																)
															: undefined
													}
												/>
											)}
										</GetFieldValue>
									</Col>
									{/* <Col xs="auto">
										<Box style={{ maxWidth: "135px" }} title="Free of cost">
											<TextInputField
												name="complimentary_count"
												label={
													<Inline alignItems="center" gap="1">
														<Text>Total FOC</Text>
														<Tooltip
															interactive
															content="Total number of free-of-cost pax in the trip, generally used for group tours."
														>
															<Box>
																<Icons.Info />
															</Box>
														</Tooltip>
													</Inline>
												}
												type="number"
												placeholder="e.g. 1"
												disabled={readOnlyPax}
											/>
										</Box>
									</Col> */}
								</Grid>
							</Stack>
						</Col>
					</Grid>
					<Divider />
					<Grid gap="4">
						<Col sm={12} md={4}>
							<GetFieldValue<IContact | undefined> name="trip_source_contact">
								{({ value }) => (
									<Stack gap="1">
										<Heading as="h3">
											<Icons.Identification size="6" /> Guest Details
											{value ? " (optional)" : null}
										</Heading>
										<Text color="muted">
											Please provide name and phone number(s).
										</Text>
									</Stack>
								)}
							</GetFieldValue>
						</Col>
						<Col>
							<Grid gap="4">
								<Col xs={12} sm={3}>
									<SelectField
										select={Select}
										placeholder="e.g. Mr."
										name={`tourist.salutation`}
										label="Salutation"
										options={["Mr.", "Mrs.", "Ms."]}
									/>
								</Col>
								<Col xs={12} sm="auto">
									<TextInputField
										name="tourist.name"
										label="Name"
										required
										placeholder="Anoop Rai"
										type="text"
										maxWidth="xs"
									/>
								</Col>
								<Col sm="auto">
									<PhoneInputField
										name={`tourist.phone_numbers`}
										label="Phone Number(s)"
										required
										multi
									/>
								</Col>
								<Col sm="auto">
									<GetFieldValue<string | undefined> name="tourist.email">
										{({ value }) => (
											<Component initialState={!!value}>
												{({ state, setState }) =>
													state || !!value ? (
														<TextInputField
															name="tourist.email"
															label="Email"
															secondaryLabel="optional"
															type="email"
															placeholder="user@domain.com"
															maxWidth="xs"
														/>
													) : (
														<Stack gap="1">
															<Box display={{ xs: "none", sm: "block" }}>
																&nbsp;
															</Box>
															<Box>
																<Button
																	onClick={() => setState(true)}
																	title="Click to add Email Address"
																>
																	<Icons.Mail />
																</Button>
															</Box>
														</Stack>
													)
												}
											</Component>
										)}
									</GetFieldValue>
								</Col>
								<Col sm="auto">
									<GetFieldValue<string | undefined> name="tourist.address">
										{({ value }) => (
											<Component initialState={!!value}>
												{({ state, setState }) =>
													state || !!value ? (
														<Inline flexWrap="wrap" gap="4">
															<AddressInputField
																name={`tourist.address`}
																label="Origin City/State"
																onlyLocation
																hidePreviewForLocation
															/>
															<SelectField
																select={SelectWorldCountries}
																name={`tourist.nationality`}
																label="Nationality"
																required
																placeholder="e.g. Indian"
																fetchOnMount
																cacheKey={`trip-detail-tourist-nationality`}
																labelKey="nationality"
																optionRenderer={({ option }) => (
																	<Text>{option.name}</Text>
																)}
															/>
														</Inline>
													) : (
														<Stack gap="1">
															<Box display={{ xs: "none", sm: "block" }}>
																&nbsp;
															</Box>
															<Box>
																<Button
																	onClick={() => setState(true)}
																	title="Click to add Origin City/State"
																>
																	<Icons.LocationMarker />
																</Button>
															</Box>
														</Stack>
													)
												}
											</Component>
										)}
									</GetFieldValue>
								</Col>
							</Grid>
						</Col>
					</Grid>

					<Grid noGutters={shouldRemoveGutters}>
						<Col lg={4} />
						<Col md={12} lg={8}>
							<ExistingTouristProfiles
								exceptId={showDuplicateExceptId}
								fieldPrefix="tourist"
							>
								{({
									touristProfiles,
									isLoading,
									selectedProfileId,
									profile,
									shouldFetch,
								}) => {
									const selectTouristProfile = (
										touristProfile: TTouristProfile
									) => {
										const tourist = form.getState().values.tourist
										const {
											type,
											salutation,
											name_without_salutation,
											dob,
											nationality,
											phone_numbers,
											email,
											address,
											gst_number,
											remarks,
											id,
										} = touristProfile

										form.change("tourist", {
											...tourist,
											type: type ?? "adult",
											salutation: salutation ?? undefined,
											name: name_without_salutation ?? "",
											phone_numbers: phone_numbers ?? [],
											email: email ?? undefined,
											address: address
												? transformAddressToEditableValue({
														...address,
														billing_details: gst_number,
													})
												: undefined,
											nationality: nationality ?? undefined,
											dob: dob ? parseDate(dob) : undefined,
											remarks: remarks ?? undefined,
											profile_id: id,
											profile: touristProfile,
										})
									}
									const unSelectTouristProfile = () => {
										const tourist = form.getState().values.tourist

										form.change("tourist", {
											...tourist,
											type: "adult",
											salutation: undefined,
											name: "",
											phone_numbers: [],
											email: "",
											address: undefined,
											nationality: undefined,
											dob: undefined,
											remarks: undefined,
											profile_id: null,
											profile: undefined,
										})
									}

									const profilesToShow = touristProfiles
										? profile
											? [
													profile,
													...touristProfiles.data.filter(
														(t) => t.id !== profile.id
													),
												]
											: touristProfiles.data
										: profile
											? [profile]
											: null

									return (
										<Stack
											paddingTop="4"
											justifyContent="center"
											style={{ minHeight: "115px" }}
										>
											{isLoading ? (
												<Inline
													alignItems="center"
													justifyContent="center"
													gap="3"
												>
													<Icons.Refresh
														cursor="pointer"
														spin={isLoading}
														color="muted"
													/>
													<Text color="muted">
														Checking for existing tourist profiles....
													</Text>
												</Inline>
											) : profilesToShow ? (
												<TouristProfilesTableView
													selectedProfileId={selectedProfileId}
													touristProfiles={profilesToShow}
													selectTouristProfile={selectTouristProfile}
													unSelectTouristProfile={unSelectTouristProfile}
												/>
											) : shouldFetch ? (
												<Alert status="warning">
													No existing tourist profiles were found for the
													provided details
												</Alert>
											) : (
												<Alert status="info">
													Enter a name, phone number or email address to check
													for existing tourist profiles.
												</Alert>
											)}
										</Stack>
									)
								}}
							</ExistingTouristProfiles>
						</Col>
					</Grid>
					<ExistingTrips exceptId={showDuplicateExceptId}>
						{({ trips }) =>
							trips ? (
								<Box paddingTop="2">
									<Alert status="warning" title="Possible Duplicate Query">
										<Stack gap="4">
											<Text>
												There are some ({trips.meta.total}) existing queries
												with above phone numbers. Please review the details
												before proceeding further.
											</Text>
											<Box color="default">
												<QueriesTableView items={trips.data} />
											</Box>
										</Stack>
									</Alert>
								</Box>
							) : null
						}
					</ExistingTrips>
					{!hideComments ? (
						<>
							<Divider />
							<Grid gap="4">
								<Col sm={12} md={4}>
									<Stack gap="1">
										<Heading as="h3">
											<Icons.Annotation size="6" /> Comments or Notes
										</Heading>
										<Box color="muted">
											Please provide any comments or notes regarding this query
											which may be useful for sales process.
										</Box>
									</Stack>
								</Col>
								<Col>
									<TextAreaInputField
										name="comments"
										label="Comments"
										secondaryLabel="optional"
										placeholder="Only 5 star hotels"
									/>
								</Col>
							</Grid>
						</>
					) : null}
					<Divider />
					<Grid gap="4">
						<Col sm={12} md={{ offset: 4, span: 8 }}>
							<Stack gap="4" paddingLeft={{ md: "4" }}>
								<SubmissionError />
								<Inline gap="4">
									<Button type="submit" disabled={submitting}>
										{submitting ? "Saving..." : "Save Details"}
									</Button>
									{onCancel ? (
										<Button onClick={onCancel} level="tertiary">
											Cancel
										</Button>
									) : null}
								</Inline>
							</Stack>
						</Col>
					</Grid>
				</form>
			)}
		</Form>
	)
}

function ExistingTrips({
	children,
	exceptId,
}: {
	children: (props: {
		trips: IListResponse<ITrip> | undefined
	}) => React.ReactNode
	exceptId?: number
}) {
	const { value } = useFieldValue<null | Array<{
		number: string
		country_code?: string
		is_primary: boolean
	}>>("tourist.phone_numbers")
	const validPhoneNumbers = useMemo(() => {
		return (value || [])
			.map(function (p) {
				return p.number
			})
			.filter(Boolean)
	}, [value])

	const debouncedPhoneNumbers = useDebounce(validPhoneNumbers, 500)
	const xhr = useXHR()

	const shouldFetch = debouncedPhoneNumbers.length > 0

	const { data: existing } = useSWR(
		shouldFetch ? ["existing-trips", debouncedPhoneNumbers, exceptId] : null,
		async ([_, phoneNumbers, exceptId]) => {
			const resp = await xhr.get<IListResponse<ITrip>>("/existing-trips", {
				params: {
					phone_numbers: phoneNumbers,
					with: ["given_price"],
					except_ids: exceptId ? [exceptId] : undefined,
				},
			})

			return resp.data.data.length ? resp.data : undefined
		},
		{
			dedupingInterval: 500,
		}
	)

	return (
		<>
			{children({
				trips: debouncedPhoneNumbers?.length ? existing : undefined,
			})}
		</>
	)
}

function QueriesTableView({ items }: { items: Array<ITrip> }) {
	return (
		<Table
			bordered
			responsive
			hover
			headers={["ID", "Guest", "B2B Source", "Destination", "Quote", "Team"]}
			rows={items.map((trip) => {
				const {
					destinations,
					nights: no_of_nights,
					days,
					latest_given_quote,
					given_quotes_count,
					trip_source,
					trip_source_contact,
					team,
					reference_id,
					tourist,
					no_of_adults,
					no_of_children,
					created_at,
				} = trip
				return [
					<Stack gap="1">
						<Text fontWeight="semibold">{trip.id}</Text>
						<Box fontSize="sm">
							{latest_given_quote ? (
								<Box color="primary">In Progress</Box>
							) : (
								<Box color="muted">New Query</Box>
							)}
						</Box>
					</Stack>,
					tourist ? (
						<Stack gap="px">
							<Text fontWeight="semibold">{tourist.name}</Text>
							<Box fontSize="sm" color="muted">
								{joinAttributes(
									<Text as="span">
										{no_of_adults}A
										{no_of_children ? `, ${no_of_children}C` : null}
									</Text>,
									[
										tourist.phone_numbers?.length,
										<PhoneNumber value={tourist.phone_numbers} iconOnly />,
									],
									[tourist.email, <Email value={tourist.email} iconOnly />]
								)}
							</Box>
						</Stack>
					) : null,
					<Stack gap="px">
						<Text fontWeight="semibold">{trip_source.short_name}</Text>
						{reference_id ? (
							<Text fontSize="sm">Ref# {reference_id}</Text>
						) : null}
						{trip_source_contact ? (
							<Inline alignItems="center" flexWrap="wrap" gap="2" fontSize="sm">
								<Box color="muted">{trip_source_contact.name}</Box>
								<Box display="inlineBlock">
									{joinAttributes([
										trip_source_contact.phone_numbers?.length,
										<PhoneNumber
											value={trip_source_contact.phone_numbers}
											iconOnly
										/>,
									])}
								</Box>
							</Inline>
						) : null}
					</Stack>,
					<Stack gap="px">
						<Text style={{ maxWidth: "140px" }} textOverflow="truncate">
							{destinations.map((d) => d.name).join(", ")}
						</Text>
						<Box fontSize="sm" color="muted">
							{joinAttributes(
								<Time timestamp={trip.start_date} />,
								<TimeDuration value={duration(days, "days")}>
									{no_of_nights}N
								</TimeDuration>
							)}
						</Box>
					</Stack>,
					latest_given_quote ? (
						<Box
							title={`Latest quote ${fromNow(
								utcTimestampToLocalDate(latest_given_quote.created_at)
							)}`}
						>
							<Text>{given_quotes_count} Q</Text>
							<Money
								amount={latest_given_quote.given_price}
								currency={latest_given_quote.given_currency}
								color="muted"
								fontSize="sm"
							/>
						</Box>
					) : (
						"-"
					),
					<Stack gap="px">
						{team?.length ? (
							<Stack style={{ width: "80px" }} gap="px">
								{team.map((t) => (
									<Text
										textOverflow="truncate"
										key={t.id}
										fontSize="sm"
										color="muted"
									>
										{t.name}
									</Text>
								))}
							</Stack>
						) : null}
						<RelativeTime fontSize="sm" timestamp={created_at} />
					</Stack>,
				]
			})}
		/>
	)
}

export function ExistingTouristProfiles({
	children,
	exceptId,
	fieldPrefix = "tourist",
}: {
	children: (props: {
		touristProfiles: IListResponse<TTouristProfile> | undefined
		isLoading: boolean
		selectedProfileId: number | null
		profile: TTouristProfile | undefined
		shouldFetch: boolean
	}) => React.ReactNode
	exceptId?: number
	fieldPrefix?: string
}) {
	const { value: selectedProfileId } = useFieldValue<null | number>(
		`${fieldPrefix}.profile_id`
	)
	const { value: profile } = useFieldValue<undefined | TTouristProfile>(
		`${fieldPrefix}.profile`
	)
	const { value: name } = useFieldValue<null | string>(`${fieldPrefix}.name`)
	const { value: email } = useFieldValue<null | string>(`${fieldPrefix}.email`)
	const { value: phoneNumber } = useFieldValue<null | Array<{
		number: string
		country_code?: string
		is_primary: boolean
	}>>(`${fieldPrefix}.phone_numbers`)
	const validPhoneNumbers = useMemo(() => {
		return (phoneNumber || [])
			.map(function (p) {
				return p.number
			})
			.filter(Boolean)
	}, [phoneNumber])
	const debouncedName = useDebounce(name, 500)
	const debouncedPhoneNumbers = useDebounce(validPhoneNumbers, 500)
	const debouncedEmail = useDebounce(email, 500)
	const xhr = useXHR()

	const shouldFetch = Boolean(
		debouncedName?.trim() ||
		debouncedPhoneNumbers.length ||
		debouncedEmail?.trim()
	)

	const {
		data: existingProfiles,
		isLoading,
		isValidating,
	} = useSWR(
		shouldFetch
			? [
					"existing-profiles",
					debouncedName,
					debouncedPhoneNumbers,
					debouncedEmail,
					exceptId,
				]
			: null,
		async ([_, name, phoneNumbers, email, exceptId]) => {
			const resp = await xhr.get<IListResponse<TTouristProfile>>(
				"/existing-profiles",
				{
					params: {
						q: name,
						phone_numbers: phoneNumbers,
						email: email,
						except_ids: exceptId ? [exceptId] : undefined,
					},
				}
			)

			return resp.data.data.length ? resp.data : undefined
		}
	)

	return children({
		touristProfiles: shouldFetch ? existingProfiles : undefined,
		isLoading: isLoading || isValidating,
		selectedProfileId,
		profile,
		shouldFetch,
	})
}

export function TouristProfilesTableView({
	selectedProfileId,
	touristProfiles,
	selectTouristProfile,
	unSelectTouristProfile,
}: {
	selectedProfileId: number | null
	touristProfiles: Array<TTouristProfile>
	selectTouristProfile: (touristProfile: TTouristProfile) => void
	unSelectTouristProfile: () => void
}) {
	return (
		<Table
			bordered
			responsive
			hover
			stickyLastColumn
			rows={touristProfiles.map((touristProfile) => {
				return [
					<Inline gap="3" alignItems="center">
						<TouristProfileInfo touristProfile={touristProfile} type="basic" />
					</Inline>,
					<ProfileAttribute
						label="Meal Pref."
						icon={<Icons.Meal fontSize="sm" opacity="70" />}
						value={touristProfile.meal_preference}
					/>,
					<ProfileAttribute
						label="Stay Pref."
						icon={<Icons.Bed fontSize="sm" opacity="70" />}
						value={touristProfile.stay_preference}
					/>,
					<ProfileAttribute
						label="Smoking"
						icon={<Icons.Smoking fontSize="sm" opacity="70" />}
						value={
							touristProfile.is_smoker === null
								? undefined
								: touristProfile.is_smoker
									? "Yes"
									: "No"
						}
					/>,
					<ProfileAttribute
						label="Origin City/State"
						icon={<Icons.LocationMarker fontSize="sm" opacity="70" />}
						value={touristProfile.address?.location?.name}
					/>,
					<TouristProfileActions
						touristProfile={touristProfile}
						selectedProfileId={selectedProfileId}
						selectTouristProfile={selectTouristProfile}
						unSelectTouristProfile={unSelectTouristProfile}
					/>,
				]
			})}
		/>
	)
}

function TouristProfileInfo({
	touristProfile,
	type,
}: {
	touristProfile: TTouristProfile
	type: "basic" | "detail"
}) {
	const { hasPermission } = useCheckPermissions()
	return (
		<>
			<Stack>
				<Inline
					size="12"
					alignItems="center"
					justifyContent="center"
					rounded="full"
					boxShadow="base"
					bgColor={type === "detail" ? "primary_emphasis" : "inset"}
					fontSize="md"
					fontWeight="semibold"
					borderWidth="1"
					color={type === "detail" ? "white" : "muted"}
					letterSpacing="wider"
				>
					{touristProfile.name?.slice(0, 2)}
				</Inline>
			</Stack>

			<Stack gap="1">
				<Inline gap="2">
					<Text fontWeight="semibold">{touristProfile.name}</Text>

					{hasPermission(PERMISSIONS.MANAGE_USERS) ? (
						<Link
							fontWeight="semibold"
							to={generatePath("/tourist-profiles/:touristProfileId", {
								touristProfileId: touristProfile.id.toString(),
							})}
							target="_blank"
						>
							<Box
								size="6"
								display="flex"
								justifyContent="center"
								alignItems="center"
								padding="1"
								backgroundColor="primary"
								borderColor="primary"
								borderWidth="1"
								borderRadius="md"
							>
								<Icons.User color="primary" size="20" />
							</Box>
						</Link>
					) : (
						<Box
							size="6"
							display="flex"
							justifyContent="center"
							alignItems="center"
							padding="1"
							backgroundColor="primary"
							borderColor="primary"
							borderWidth="1"
							borderRadius="md"
						>
							<Icons.User color="muted" size="20" />
						</Box>
					)}
				</Inline>

				<Box fontSize="sm" color="muted" style={{ minWidth: "160px" }}>
					{joinAttributes(
						[
							touristProfile.phone_numbers?.length,
							<PhoneNumber
								value={touristProfile.phone_numbers}
								onPointerDown={(e) => {
									e.preventDefault()
									// copyTextToClipboard(value)
								}}
							/>,
						],
						[touristProfile.email, <Email value={touristProfile.email} />]
					)}
				</Box>
			</Stack>
		</>
	)
}

function ProfileAttribute({
	label,
	icon,
	value,
}: {
	label: string
	icon: React.ReactNode
	value?: React.ReactNode | null
}) {
	return (
		<Stack gap="1" textAlign="center" width="auto">
			<Tooltip interactive content={label} placement="top">
				<Box>{icon}</Box>
			</Tooltip>

			<Text as="span" fontWeight="semibold" whiteSpace="nowrap">
				{value ?? <Text color="muted">N/A</Text>}
			</Text>
		</Stack>
	)
}

function TouristProfileActions({
	touristProfile,
	selectedProfileId,
	selectTouristProfile,
	unSelectTouristProfile,
}: {
	touristProfile: TTouristProfile
	selectedProfileId: number | null
	selectTouristProfile: (touristProfile: TTouristProfile) => void
	unSelectTouristProfile: () => void
}) {
	return (
		<Inline gap="3" justifyContent="center">
			{touristProfile.id === selectedProfileId ? (
				<Button level="secondary" size="sm" onClick={unSelectTouristProfile}>
					<Icons.BadgeCheckSolid />
					Selected
				</Button>
			) : (
				<Button
					level="primary"
					size="sm"
					onClick={() => selectTouristProfile(touristProfile)}
				>
					Use this
				</Button>
			)}

			<TouristProfileInDialog
				selectedProfileId={selectedProfileId}
				touristProfile={touristProfile}
				selectTouristProfile={selectTouristProfile}
				unSelectTouristProfile={unSelectTouristProfile}
			>
				{({ show }) => (
					<Button size="sm" level="secondary" onClick={show}>
						<Icons.ArrowExpand />
					</Button>
				)}
			</TouristProfileInDialog>
		</Inline>
	)
}

export function TouristProfileInDialog({
	selectedProfileId,
	touristProfile,
	selectTouristProfile,
	unSelectTouristProfile,
	children,
}: {
	selectedProfileId: number | null
	touristProfile: TTouristProfile
	selectTouristProfile: (touristProfile: TTouristProfile) => void
	unSelectTouristProfile: () => void
	children: (props: { show: () => void }) => React.ReactNode
}) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ show: open })}
			<Dialog
				open={isOpen}
				onClose={close}
				title={`${touristProfile?.name_without_salutation}`}
			>
				<Dialog.Body>
					<TouristProfileDetails touristProfile={touristProfile} />
				</Dialog.Body>

				<Divider sm />
				<Grid>
					<Col xs={12}>
						<Stack gap="4" paddingX="3" paddingBottom="3">
							<Inline gap="4">
								{touristProfile.id === selectedProfileId ? (
									<Button level="secondary" onClick={unSelectTouristProfile}>
										<Icons.BadgeCheckSolid />
										Selected
									</Button>
								) : (
									<Button
										level="primary"
										onClick={async () => {
											selectTouristProfile(touristProfile)
											close()
										}}
									>
										Use This
									</Button>
								)}
								<Button onClick={close} level="tertiary">
									Cancel
								</Button>
							</Inline>
						</Stack>
					</Col>
				</Grid>
			</Dialog>
		</>
	)
}

export function TouristProfileDetails({
	touristProfile,
}: Readonly<{
	touristProfile: TTouristProfile
}>) {
	return (
		<Stack gap="4" padding="1">
			<Inline
				gap="4"
				padding="4"
				rounded="lg"
				bgColor="primary"
				flexWrap="wrap"
				justifyContent="start"
			>
				<TouristProfileInfo touristProfile={touristProfile} type="detail" />
			</Inline>
			<Box
				borderWidth="1"
				borderColor="muted"
				rounded="lg"
				paddingY="2"
				paddingX="6"
			>
				<Inline gap="10" flexWrap="wrap" justifyContent="start">
					<ProfileAttribute
						label="Date of Birth"
						icon={<Icons.Cake fontSize="sm" opacity="70" />}
						value={
							touristProfile.dob
								? formatDate(touristProfile.dob, "DD MMM YYYY")
								: null
						}
					/>
					<ProfileAttribute
						label="Meal Pref."
						icon={<Icons.Meal fontSize="sm" opacity="70" />}
						value={touristProfile.meal_preference}
					/>
					<ProfileAttribute
						label="Stay Pref."
						icon={<Icons.Bed fontSize="sm" opacity="70" />}
						value={touristProfile.stay_preference}
					/>
					<ProfileAttribute
						label="Smoking"
						icon={<Icons.Smoking fontSize="sm" opacity="70" />}
						value={
							touristProfile.is_smoker === null
								? null
								: touristProfile.is_smoker
									? "Yes"
									: "No"
						}
					/>
					<ProfileAttribute
						label="Origin City/State"
						icon={<Icons.LocationMarker fontSize="sm" opacity="70" />}
						value={touristProfile.address?.location?.name}
					/>
					<ProfileAttribute
						label="Language Pref."
						icon={<Icons.Language fontSize="sm" opacity="70" />}
						value={touristProfile.language_preference}
					/>
					<ProfileAttribute
						label="Preferred Airport"
						icon={<Icons.Airport fontSize="sm" opacity="70" />}
						value={touristProfile.preferred_airport?.name}
					/>
					<ProfileAttribute
						label="Flight Class Pref."
						icon={<Icons.Airplane fontSize="sm" opacity="70" />}
						value={touristProfile.preferred_flight_class}
					/>
					<ProfileAttribute
						label="Passport Number"
						icon={<Icons.Passport fontSize="sm" opacity="70" />}
						value={touristProfile.passport_number}
					/>

					<ProfileAttribute
						label="Passport Expiry Date"
						icon={<Icons.PassportExpiry fontSize="sm" opacity="70" />}
						value={
							touristProfile.passport_expiry_date
								? formatDate(touristProfile.passport_expiry_date, "DD MMM YYYY")
								: null
						}
					/>
				</Inline>
			</Box>
			<Stack gap="2">
				{(touristProfile?.trips?.length ?? 0 > 1) ? (
					<>
						<Heading as="h6" fontSize="base">
							{`Past ${touristProfile.trips?.length} Trips`}
						</Heading>

						<Box
							style={{
								maxHeight: 300,
								overflowY: "auto",
							}}
						>
							<Table
								bordered
								responsive
								hover
								headers={["Trip ID", "Guest", "Details", "Amount"]}
								rows={touristProfile.trips?.map((trip) => {
									const {
										destinations,
										nights: no_of_nights,
										days,
										is_end_date_passed,
										cancellation_reason,
										is_live,
										converted_at,
										on_hold_at,
										latest_given_quote,
									} = trip

									const start_date = localOrUtcTimestampToLocalDate(
										trip.start_date_local,
										trip.start_date
									)

									return [
										<Stack gap="px">
											<Box>
												<IDCell trip={trip} />
											</Box>
											<Box fontSize="sm">
												{cancellation_reason ? (
													<Badge
														warning={!converted_at}
														danger={Boolean(converted_at)}
														outlined
														title={cancellation_reason.reason}
													>
														{converted_at ? "Dropped" : "Cancelled"}
													</Badge>
												) : converted_at ? (
													is_live ? (
														<Box color="accent">
															<Ping title="On Trip" /> On Trip
														</Box>
													) : is_end_date_passed ? (
														<Box color="muted">Past Trip</Box>
													) : (
														<Badge success>Converted</Badge>
													)
												) : on_hold_at ? (
													<Badge success outlined>
														On-Hold
													</Badge>
												) : latest_given_quote ? (
													<Box color="primary">In Progress</Box>
												) : (
													<Box color="muted">New Query</Box>
												)}
											</Box>
										</Stack>,
										<TripTourist trip={trip} />,
										<Stack gap="px">
											<Destinations destinations={destinations} />
											<Box fontSize="sm" color="muted">
												{joinAttributes(
													<Time value={start_date} />,
													<TimeDuration value={duration(days, "days")}>
														{no_of_nights}N
													</TimeDuration>,
													getDiff(new Date(), start_date, "month") === 0 ? (
														<RelativeTime
															color="muted"
															fontSize="xs"
															value={start_date}
														/>
													) : null
												)}
											</Box>
										</Stack>,
										<Box>
											{latest_given_quote ? (
												<Box>
													<Money
														amount={latest_given_quote?.given_price}
														currency={
															latest_given_quote?.given_currency || "INR"
														}
														showCurrency
													/>
												</Box>
											) : (
												<Text fontWeight="semibold" color="muted">
													N/A
												</Text>
											)}
										</Box>,
									]
								})}
							/>
						</Box>
					</>
				) : (
					<>
						<Heading as="h6" fontSize="base" color="muted" textAlign="center">
							No Trips Yet
						</Heading>
					</>
				)}
			</Stack>
		</Stack>
	)
}
